import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_type: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const serviceTypes = [
    'Income Tax Filing',
    'GST Filing',
    'Business Registration',
    'Tax Planning',
    'Audit Services',
    'Compliance Services',
    'Other',
  ];

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '+91-9717705640',
      subtext: '24/7 Support Available',
      href: 'tel:+919717705640',
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'info@taxcompany.in',
      subtext: 'We reply within 24 hours',
      href: 'mailto:info@taxcompany.in',
    },
    {
      icon: MapPin,
      title: 'Office',
      value: 'B-248 2nd Floor North Ex Mall',
      subtext: 'Rohini Sector 9 Delhi 110085',
      href: '#',
    },
    {
      icon: Clock,
      title: 'Business Hours',
      value: 'Mon - Fri: 10AM - 7PM',
      subtext: 'Closed on Weekends',
      href: '#',
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        source_page: 'Contact Page'
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_type: '',
        message: '',
      });
    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        data-testid="contact-hero"
        className="relative py-20 md:py-32 overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full text-blue-400 text-sm font-medium mb-6">
              Contact Us
            </span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white mb-6 leading-tight">
              Let's Start a{' '}
              <span className="text-gradient">Conversation</span>
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed">
              Have questions about our services? Need help with your taxes? 
              We're here to help. Reach out to us and our team will get back to you shortly.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section 
        data-testid="contact-info-section"
        className="py-12 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <a
                key={info.title}
                href={info.href}
                data-testid={`contact-info-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <info.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-white mb-1">
                  {info.title}
                </h3>
                <p className="text-blue-400 font-medium">{info.value}</p>
                <p className="text-slate-400 text-sm">{info.subtext}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section 
        data-testid="contact-form-section"
        className="py-20 md:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column - Info */}
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Get In Touch</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Ready to Get Started?
              </h2>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Fill out the form and our team will reach out to you within 24 hours. 
                We offer free initial consultations to understand your needs better.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-white">Free Consultation</h4>
                    <p className="text-slate-400 text-sm">Get expert advice at no cost</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-white">Quick Response</h4>
                    <p className="text-slate-400 text-sm">We respond within 24 hours</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-white">Expert Team</h4>
                    <p className="text-slate-400 text-sm">Certified professionals at your service</p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="mt-12">
                <div 
                  data-testid="map-placeholder"
                  className="bg-navy-800 border border-white/5 rounded-2xl h-64 flex items-center justify-center"
                >
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                    <p className="text-white font-medium">Our Office Location</p>
                    <p className="text-slate-400 text-sm">B-248 2nd Floor North Ex Mall Rohini Sector 9 Delhi 110085</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-navy-800 border border-white/5 rounded-2xl p-8 md:p-10">
              {isSubmitted ? (
                <div 
                  data-testid="form-success-message"
                  className="text-center py-12"
                >
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-white mb-3">
                    Thank You!
                  </h3>
                  <p className="text-slate-400 mb-6">
                    We've received your message and will get back to you within 24 hours.
                  </p>
                  <Button
                    data-testid="send-another-button"
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <h3 className="font-heading font-bold text-xl text-white mb-6">
                    Send Us a Message
                  </h3>

                  {error && (
                    <div 
                      data-testid="form-error"
                      className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400"
                    >
                      {error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="input-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@example.com"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="input-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91-9717705640"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service_type" className="text-slate-300">Service Type *</Label>
                    <Select 
                      onValueChange={handleServiceChange} 
                      value={formData.service_type}
                      required
                    >
                      <SelectTrigger 
                        data-testid="select-service"
                        className="bg-navy-900 border-white/10 text-white focus:border-blue-500"
                      >
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/10">
                        {serviceTypes.map((service) => (
                          <SelectItem 
                            key={service} 
                            value={service}
                            className="text-white hover:bg-navy-700 focus:bg-navy-700"
                          >
                            {service}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-slate-300">Your Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="input-message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your needs..."
                      className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500 focus:border-blue-500 resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    data-testid="submit-button"
                    disabled={isSubmitting}
                    className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>

                  <p className="text-slate-500 text-sm text-center">
                    By submitting this form, you agree to our privacy policy.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
